import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button } from "antd";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useGetData from "../../api/useGetData";
import usePostData from "../../api/usePostData";
import { getArrivalTime } from "../../utils/getArrivalTime";
import PayByLinkQr from "./PayByLink";
import usePaymentMethods from "./usePaymentMethods";
import { paymentSelectTracking } from "../../utils/mixpanel";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

/**
 * A component that shows the users a list of available payment methods. It gets shown to them once the booking data was submitted successfully.
 * Once the user selects one of the available payment methods, an action is taken depending on the selected method.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */

const PaymentSelect = ({ setActiveTab, formValues, serviceType }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("global");
  const { state } = useLocation();
  const { postData: getRideCost, res: rideCostData } = usePostData();
  const [qrCode, setQrCode] = useState(state?.qrLink || null);
  const rideCost =
    rideCostData?.data?.data?.price ||
    (state?.qrLink ? state?.price : undefined);
  const proposal = rideCostData?.data?.data?.proposal;
  const { user } = useSelector((state) => state.authData);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [isMac, setIsMac] = useState(false);
  const bookingSettings = useSelector(
    (state) => state.cachedRequests.bookingSettings
  );
  const { cashPayment, creditCardPayment, payByLink } = usePaymentMethods({
    formValues,
    proposal,
    rideCost,
    selectedMethod: getPaymentMethod(selectedMethod),
  });
  const bookingAllowedTime = (bookingSettings?.booking_allowed_time ?? 0) / 60;
  const { data: paymentMethodsData, loading: loadingPaymentsMethods } =
    useGetData({
      route: "/api/method/airport_transport.api.bookings.get_payment_methods",
      params: {
        arrival_date: formValues.arrivalDate.format("YYYY-MM-DD"),
        arrival_time: getArrivalTime(
          formValues.arrivalTime,
          bookingAllowedTime,
          formValues.arrivalDate
        ),
      },
    });
    
    useEffect(() => {
      const ua = navigator.userAgent || "";
      const maxTouchPoints = navigator.maxTouchPoints || 0;
    
      const userAgentData = navigator.userAgentData;
      let platform = "";
    
      // Use userAgentData.platform if available (newer, non-deprecated)
      if (userAgentData && typeof userAgentData.platform === "string") {
        platform = userAgentData.platform;
      } else if (typeof navigator.userAgent === "string") {
        // As a fallback, derive from the userAgent string
        if (/Macintosh/i.test(ua)) platform = "MacIntel";
        else if (/iPhone|iPad|iPod/i.test(ua)) platform = "iPhone";
        else platform = "Unknown";
      }
    
      const isIOS =
        /iP(hone|od|ad)/i.test(ua) ||
        (platform === "MacIntel" && maxTouchPoints > 1);
    
      const isMac = platform.startsWith("Mac");
      const isSupported = isIOS || isMac;
    
      setIsMac(isSupported);
    }, []);
    
    
    
    

  if (loadingPaymentsMethods) {
    return <div className="min-h-[232px]"></div>;
  }
  const onPaymentMethodSelect = (paymentMethod) => {
    setSelectedMethod(paymentMethod);
    paymentSelectTracking({
      user: user?.email,
      service: serviceType,
      method: paymentMethod,
    });
    getRideCost({
      route: "/api/method/airport_transport.api.integrations.maps.get_price",
      data: {
        location: `${formValues.locationCoords.lat},${formValues.locationCoords.lng}`,
        destination:
          formValues.destinationCoords &&
          `${formValues.destinationCoords.lat},${formValues.destinationCoords.lng}`,
        vehicle_type: formValues.vehicleType,
        rider: formValues.sharedRide ? formValues.seatNumber.toString() : "0",
        arrival_date: formValues.arrivalDate.format("YYYY-MM-DD"),
        arrival_time: getArrivalTime(
          formValues.arrivalTime,
          bookingAllowedTime,
          formValues.arrivalDate
        ),
        shared_discount:
          formValues.sharedRide && formValues.discount
            ? formValues.discount
            : undefined,
        service_type: serviceType,
        payment_method: paymentMethod,
        zone: formValues.zone,
        language: language,
        hours: formValues.hours || "",
        user: formValues.agentUser || undefined,
      },
    });
  };
  const onSubmit = async () => {
    const paymentMethod = getPaymentMethod(selectedMethod);
    if (paymentMethod === "Cash" ) { 
      cashPayment();
    } 
    else if (paymentMethod === "Apple Pay") {
      creditCardPayment();
    }
    else if (paymentMethod === "Mada" || paymentMethod === "Credit Card") {
      creditCardPayment();
    } else {
      setQrCode(await payByLink());
    }
  };





  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 items-center">
        <button
          onClick={() => {
            if (state) {
              delete state.qrLink;
              delete state.price;
            }
            setActiveTab(2);
          }}
          className="text-xl"
        >
          <ArrowLeftOutlined rotate={language === "ar" ? 180 : 0} />
        </button>
        {rideCost && (
          <div className="flex flex-col md:flex-row justify-center text-xl">
            <div className="mx-2">{t("hero.your_cost_text")}</div>
            <div className="mx-0.5 font-bold text-background_steel_blue">
              {rideCost} {t("hero.sar_text")}
            </div>
          </div>
        )}
      </div>
      <div>
        {qrCode ? (
          <PayByLinkQr
            email={user?.agent ? formValues.agentUser : user.email}
            payByLinkPaymentLink={qrCode.link}
            qrCode={qrCode.qrCode}
          />
        ) : (
          <>
            {paymentMethodsData.length === 0 && (
              <p className="text-center mt-5">
                {t("payment_not_available_text")}
              </p>
            )}
            {paymentMethodsData.map((method) => {
                if ((method.payment_method === "Apple Pay" || method.payment_method ===  'آبل باي') && !isMac) {
                  return null;
                }

                return (
                  <button
                    key={method.payment_method}
                    className={clsx(
                      "flex flex-row justify-between items-center py-3 border-b border-border_color w-full",
                      selectedMethod === method.payment_method &&
                        "bg-background_darkgrey rounded-md"
                    )}
                    onClick={() => {
                      onPaymentMethodSelect(method.payment_method);
                    }}
                  >
                    <div className="flex flex-row justify-between items-center">
                      <div className="mx-2">
                        <img src={method.icon} alt={method.icon} />
                      </div>
                      <div className="mx-2">{method.payment_method}</div>
                    </div>
                  </button>
                );
              })}

            {rideCost && (
              <Button
                className="mt-3 bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2"
                onClick={onSubmit}
                type="button"
              >
                {t("processed_text")}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const getPaymentMethod = (paymentMethod) => {
  const arMap = {
    "الدفع نقدًا": "Cash",
    "الدفع من خلال لينك": "Pay By Link",
    "الدفع من خلال مدي": "Mada",
    "الدفع من خلال بطاقة": "Credit Card",
    "آبل باي":"Apple Pay"
  };
  return arMap[paymentMethod] || paymentMethod;
};

PaymentSelect.propTypes = {
  setActiveTab: PropTypes.func.isRequired,
  serviceType: PropTypes.string.isRequired,
  formValues: PropTypes.shape({
    arrivalDate: PropTypes.object.isRequired, // moment object
    arrivalTime: PropTypes.object.isRequired,
    locationCoords: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
    destinationCoords: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    vehicleType: PropTypes.string.isRequired,
    sharedRide: PropTypes.bool,
    seatNumber: PropTypes.number,
    discount: PropTypes.number,
    zone: PropTypes.string,
    hours: PropTypes.string,
    agentUser: PropTypes.string,
  }).isRequired,
};

export default PaymentSelect;
